// hooks/useGlobalImagePreloader.js
import { useEffect } from 'react';
import { imageCategories, preloadImages } from '../utilities';

/**
 * Hook para precargar imágenes globalmente en la aplicación
 * Se ejecuta una sola vez al montar la aplicación
 * @param {Object} options - Opciones de configuración
 * @param {boolean} options.enabled - Si está habilitada la precarga (default: true)
 * @param {number} options.delay - Delay inicial antes de comenzar (default: 2000ms)
 * @param {number} options.batchSize - Tamaño del lote (default: 3)
 * @param {number} options.batchDelay - Delay entre lotes (default: 500ms)
 */
export const useGlobalImagePreloader = (options = {}) => {
  const {
    enabled = true,
    delay = 2000, // Esperar 2 segundos después del montaje
    batchSize = 3,
    batchDelay = 500
  } = options;

  useEffect(() => {
    if (!enabled) return;

    const preloadAllImages = async () => {
      try {
        // Esperar el delay inicial para no interferir con la carga inicial
        await new Promise(resolve => setTimeout(resolve, delay));

        // Obtener todas las imágenes
        const allImages = Object.values(imageCategories).flat();

        // console.log(`🖼️ Iniciando precarga global de ${allImages.length} imágenes...`);

        // Precargar en lotes pequeños para no sobrecargar
        for (let i = 0; i < allImages.length; i += batchSize) {
          const batch = allImages.slice(i, i + batchSize);

          try {
            await Promise.allSettled(
              batch.map(async (src) => {
                try {
                  await preloadImages([src]);
                  // console.log(`✅ Precargada: ${src.split('/').pop()}`);
                } catch (error) {
                  console.warn(`❌ Error precargando: ${src.split('/').pop()}`);
                }
              })
            );
          } catch (error) {
            console.warn('Error en lote de precarga:', error);
          }

          // Pausa entre lotes
          if (i + batchSize < allImages.length) {
            await new Promise(resolve => setTimeout(resolve, batchDelay));
          }
        }

        // console.log('🎉 Precarga global completada');
      } catch (error) {
        console.error('Error durante la precarga global:', error);
      }
    };

    preloadAllImages();
  }, [enabled, delay, batchSize, batchDelay]);
};

export default useGlobalImagePreloader;